import { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { formatDisplayValue } from '@/lib/formatDisplayValue';

import { PreviewRow } from './PreviewRow';

import type { SubmissionWarning } from '@/lib/submissionWarnings';
import type { SubmissionDetail } from '@/types/submission.types';

interface ApplicationTabProps {
  detail: SubmissionDetail;
  warnings?: SubmissionWarning[];
}

function formatAddress(address?: string, city?: string, state?: string, zip?: string): string | null {
  const parts = [address, city, state, zip].filter(Boolean);
  return parts.length > 0 ? parts.join(', ') : null;
}

export function ApplicationTab({ detail, warnings }: ApplicationTabProps) {
  const { t } = useTranslation();

  const yesNoLabels = useMemo(() => ({ yesLabel: t('common.yes'), noLabel: t('common.no') }), [t]);

  const findWarning = useCallback((field: string) => warnings?.find((w) => w.field === field), [warnings]);
  const isFL = detail.stepA.state === 'FL';
  const isNY = detail.stepA.state === 'NY';

  return (
    <div className='space-y-6'>
      {/* Personal Information */}
      <div className='bg-white shadow rounded-lg p-6'>
        <h3 className='font-bold text-lg text-gray-800 mb-4 border-b pb-2'>
          {t('submissionDetail.application.personal')}
        </h3>
        <div className='text-sm text-gray-600 space-y-2'>
          <PreviewRow label={t('submissionDetail.application.firstName')} value={detail.stepA.firstName} />
          <PreviewRow label={t('submissionDetail.application.middleName')} value={detail.stepA.middleName} />
          <PreviewRow label={t('submissionDetail.application.lastName')} value={detail.stepA.lastName} />
          <PreviewRow label={t('submissionDetail.application.preferredName')} value={detail.stepA.preferredName} />
          <PreviewRow label={t('submissionDetail.application.email')} value={detail.stepA.email} />
          <PreviewRow label={t('submissionDetail.application.phone')} value={detail.stepA.phone} />
          <PreviewRow
            label={t('submissionDetail.application.address')}
            value={formatAddress(detail.stepA.address, detail.stepA.city, detail.stepA.state, detail.stepA.zip)}
          />
          <PreviewRow label={t('submissionDetail.application.language')} value={detail.stepA.language} />
        </div>
      </div>

      {/* Eligibility */}
      <div className='bg-white shadow rounded-lg p-6'>
        <h3 className='font-bold text-lg text-gray-800 mb-4 border-b pb-2'>
          {t('submissionDetail.application.eligibility')}
        </h3>
        <div className='text-sm text-gray-600 space-y-2'>
          <PreviewRow
            label={t('submissionDetail.application.ageConfirmation')}
            value={formatDisplayValue(detail.stepB.isOver18, yesNoLabels)}
          />
          <PreviewRow
            label={t('submissionDetail.application.workAuthorization')}
            value={formatDisplayValue(detail.stepB.isAuthorizedToWork, yesNoLabels)}
          />
        </div>
      </div>

      {/* Licensing */}
      <div className='bg-white shadow rounded-lg p-6'>
        <h3 className='font-bold text-lg text-gray-800 mb-4 border-b pb-2'>
          {t('submissionDetail.application.licensing')}
        </h3>
        {detail.stepC.licensingNotApplicable ? (
          <div className='mb-3 rounded-md border border-amber-200 bg-amber-50 px-3 py-2 text-sm text-amber-800'>
            {t('submissionDetail.application.licensingNotApplicableBanner')}
          </div>
        ) : null}
        <div className='text-sm text-gray-600 space-y-2'>
          {isFL ? (
            <>
              <PreviewRow
                label={t('submissionDetail.application.hasClassD')}
                value={formatDisplayValue(detail.stepC.florida?.hasClassD ?? false, yesNoLabels)}
              />
              <PreviewRow
                label={t('submissionDetail.application.licenseNumber')}
                value={detail.stepC.florida?.classDNumber}
              />
              <PreviewRow
                label={t('submissionDetail.application.expirationDate')}
                value={detail.stepC.florida?.classDExpiration}
                warning={findWarning('classDExpiration')}
              />
              <PreviewRow
                label={t('submissionDetail.application.hasClassG')}
                value={formatDisplayValue(detail.stepC.florida?.hasClassG ?? false, yesNoLabels)}
              />
              <PreviewRow
                label={t('submissionDetail.application.licenseNumber')}
                value={detail.stepC.florida?.classGNumber}
              />
              <PreviewRow
                label={t('submissionDetail.application.expirationDate')}
                value={detail.stepC.florida?.classGExpiration}
                warning={findWarning('classGExpiration')}
              />
            </>
          ) : null}

          {isNY ? (
            <>
              <PreviewRow
                label={t('submissionDetail.application.hasGuardCard')}
                value={formatDisplayValue(detail.stepC.newYork?.hasGuardCard ?? false, yesNoLabels)}
              />
              <PreviewRow
                label={t('submissionDetail.application.cardNumber')}
                value={detail.stepC.newYork?.guardCardNumber}
              />
              <PreviewRow
                label={t('submissionDetail.application.expirationDate')}
                value={detail.stepC.newYork?.guardCardExpiration}
                warning={findWarning('guardCardExpiration')}
              />
              <PreviewRow
                label={t('submissionDetail.application.has8HourCert')}
                value={formatDisplayValue(detail.stepC.newYork?.has8HourCert ?? false, yesNoLabels)}
              />
              <PreviewRow
                label={t('submissionDetail.application.completionDate')}
                value={detail.stepC.newYork?.cert8HourDate}
              />
              <PreviewRow
                label={t('submissionDetail.application.has16HourCert')}
                value={formatDisplayValue(detail.stepC.newYork?.has16HourCert ?? false, yesNoLabels)}
              />
              <PreviewRow
                label={t('submissionDetail.application.completionDate')}
                value={detail.stepC.newYork?.cert16HourDate}
              />
              <PreviewRow
                label={t('submissionDetail.application.hasFirearmCreds')}
                value={formatDisplayValue(detail.stepC.newYork?.hasFirearmCreds ?? false, yesNoLabels)}
              />
              <PreviewRow
                label={t('submissionDetail.application.credentialNumber')}
                value={detail.stepC.newYork?.firearmCredsNumber}
              />
              <PreviewRow
                label={t('submissionDetail.application.expirationDate')}
                value={detail.stepC.newYork?.firearmCredsExpiration}
                warning={findWarning('firearmCredsExpiration')}
              />
            </>
          ) : null}

          <div className='pt-2 border-t border-gray-100'>
            <h4 className='font-medium text-gray-700 mb-2'>{t('submissionDetail.application.certifications')}</h4>
            <PreviewRow
              label={t('submissionDetail.application.hasCprAed')}
              value={formatDisplayValue(detail.stepC.certifications.hasCprAed, yesNoLabels)}
            />
            <PreviewRow
              label={t('submissionDetail.application.expirationDate')}
              value={detail.stepC.certifications.cprAedExpiration}
              warning={findWarning('cprAedExpiration')}
            />
            <PreviewRow
              label={t('submissionDetail.application.hasTwic')}
              value={formatDisplayValue(detail.stepC.certifications.hasTwic, yesNoLabels)}
            />
            <PreviewRow
              label={t('submissionDetail.application.expirationDate')}
              value={detail.stepC.certifications.twicExpiration}
              warning={findWarning('twicExpiration')}
            />
          </div>
        </div>
      </div>

      {/* Availability */}
      <div className='bg-white shadow rounded-lg p-6'>
        <h3 className='font-bold text-lg text-gray-800 mb-4 border-b pb-2'>
          {t('submissionDetail.application.availability')}
        </h3>
        <div className='text-sm text-gray-600 space-y-2'>
          <PreviewRow
            label={t('submissionDetail.application.desiredRoles')}
            value={formatDisplayValue(detail.stepD.roles)}
          />
          <PreviewRow
            label={t('submissionDetail.application.locationPreferences')}
            value={formatDisplayValue(detail.stepD.locations)}
          />
          <PreviewRow
            label={t('submissionDetail.application.shiftPreferences')}
            value={formatDisplayValue(detail.stepD.shifts)}
          />
          <PreviewRow
            label={t('submissionDetail.application.reliableTransport')}
            value={formatDisplayValue(detail.stepD.hasTransport, yesNoLabels)}
          />
          <PreviewRow label={t('submissionDetail.application.earliestStartDate')} value={detail.stepD.startDate} />
        </div>
      </div>

      {/* Preferences */}
      <div className='bg-white shadow rounded-lg p-6'>
        <h3 className='font-bold text-lg text-gray-800 mb-4 border-b pb-2'>
          {t('submissionDetail.application.preferences')}
        </h3>
        <div className='text-sm text-gray-600 space-y-2'>
          <PreviewRow
            label={t('submissionDetail.application.indoorOutdoorComfort')}
            value={formatDisplayValue(detail.stepE.indoorOutdoorComfort)}
          />
          <PreviewRow
            label={t('submissionDetail.application.standingPatrolTolerance')}
            value={formatDisplayValue(detail.stepE.standingPatrolTolerance)}
          />
          <PreviewRow
            label={t('submissionDetail.application.languagesSpoken')}
            value={formatDisplayValue(detail.stepE.languagesSpoken)}
          />
          <PreviewRow
            label={t('submissionDetail.application.veteranStatus')}
            value={formatDisplayValue(detail.stepE.isVeteran, yesNoLabels)}
          />
        </div>
      </div>

      {/* Consents */}
      <div className='bg-white shadow rounded-lg p-6'>
        <h3 className='font-bold text-lg text-gray-800 mb-4 border-b pb-2'>
          {t('submissionDetail.application.consents')}
        </h3>
        <div className='text-sm text-gray-600 space-y-2'>
          <PreviewRow
            label={t('submissionDetail.application.consentPrivacy')}
            value={formatDisplayValue(detail.stepF.consentPrivacy, yesNoLabels)}
          />
          <PreviewRow
            label={t('submissionDetail.application.consentMessaging')}
            value={formatDisplayValue(detail.stepF.consentMessaging, yesNoLabels)}
          />
          <PreviewRow
            label={t('submissionDetail.application.consentVideoRecording')}
            value={formatDisplayValue(detail.stepF.consentVideoRecording, yesNoLabels)}
          />
          <PreviewRow label={t('submissionDetail.application.signature')} value={detail.stepF.signature} />
          <PreviewRow label={t('submissionDetail.application.signatureDate')} value={detail.stepF.signatureDate} />

          {detail.consentRecords.length > 0 ? (
            <div className='pt-2 border-t border-gray-100'>
              <h4 className='font-medium text-gray-700 mb-2'>{t('submissionDetail.application.consentedAt')}</h4>
              {detail.consentRecords.map((record, index) => (
                <PreviewRow
                  key={`${record.consentType}-${index}`}
                  label={record.consentType}
                  value={`${formatDisplayValue(record.consented, yesNoLabels)} — ${new Date(record.consentedAt).toLocaleString()} (v${record.version})`}
                />
              ))}
            </div>
          ) : null}
        </div>
      </div>
    </div>
  );
}
